import { useMemo, useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../utils/components/ui/dialog";
import { Button } from "../../utils/components/ui/button";
import { Input } from "../../utils/components/ui/input";
import { NewVendorFormValues } from "../../types/vendorManagement";

type NewVendorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: NewVendorFormValues) => void;
  isSubmitting?: boolean;
  initialData?: NewVendorFormValues;
  isEdit?: boolean;
};

const initialValues: NewVendorFormValues = {
  vendorName: "",
  contactPerson: "",
  phoneNumber: "",
  emailAddress: "",
  vatin: "",
  creditPeriod: "",
  creditLimit: "",
  openingBalance: "",
};

function NewVendorDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  initialData,
  isEdit = false,
}: NewVendorDialogProps) {
  const [formValues, setFormValues] = useState<NewVendorFormValues>(
    initialData || initialValues
  );

  useEffect(() => {
    if (open) {
      setFormValues(initialData || initialValues);
    }
  }, [open, initialData]);

  const isSubmitDisabled = useMemo(() => {
    const hasEmpty = Object.values(formValues).some((value) => typeof value === 'string' && !value.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9\s\-()]+$/;
    const isEmailInvalid = formValues.emailAddress && !emailRegex.test(formValues.emailAddress);
    const isPhoneInvalid = formValues.phoneNumber && !phoneRegex.test(formValues.phoneNumber);
    
    return hasEmpty || isEmailInvalid || isPhoneInvalid;
  }, [formValues]);

  const updateField = (field: keyof NewVendorFormValues, value: string) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setFormValues(initialValues);
    }
  };

  const handleSubmit = () => {
    onSubmit(formValues);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[960px] rounded-[24px] border-none p-0 shadow-[0_24px_60px_rgba(0,0,0,0.16)] sm:max-w-[960px]">
        <div className="p-8 sm:p-10">
          <DialogHeader className="mb-8 space-y-2">
            <DialogTitle className="text-[44px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
              {isEdit ? "Edit Vendor" : "New Vendor"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Vendor Name
              </label>
              <Input
                value={formValues.vendorName}
                onChange={(event) => updateField("vendorName", event.target.value)}
                placeholder="Enter"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Contact Person
              </label>
              <Input
                value={formValues.contactPerson}
                onChange={(event) =>
                  updateField("contactPerson", event.target.value)
                }
                placeholder="Enter"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Phone Number
              </label>
              <Input
                value={formValues.phoneNumber}
                onChange={(event) => updateField("phoneNumber", event.target.value)}
                placeholder="Enter"
                className={`h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none ${formValues.phoneNumber && !/^\+?[0-9\s\-()]+$/.test(formValues.phoneNumber) ? "ring-2 ring-red-500" : ""}`}
              />
              {formValues.phoneNumber && !/^\+?[0-9\s\-()]+$/.test(formValues.phoneNumber) && (
                <p className="text-red-500 text-xs mt-1">Invalid phone number</p>
              )}
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Email Address
              </label>
              <Input
                value={formValues.emailAddress}
                onChange={(event) =>
                  updateField("emailAddress", event.target.value)
                }
                placeholder="Enter"
                className={`h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none ${formValues.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.emailAddress) ? "ring-2 ring-red-500" : ""}`}
              />
              {formValues.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.emailAddress) && (
                <p className="text-red-500 text-xs mt-1">Invalid email address</p>
              )}
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                VATIN
              </label>
              <Input
                value={formValues.vatin}
                onChange={(event) => updateField("vatin", event.target.value)}
                placeholder="Enter"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Credit Period (in days)
              </label>
              <Input
                value={formValues.creditPeriod}
                onChange={(event) =>
                  updateField("creditPeriod", event.target.value)
                }
                placeholder="Enter in Days"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Credit Limit
              </label>
              <Input
                value={formValues.creditLimit}
                onChange={(event) => updateField("creditLimit", event.target.value)}
                placeholder="Enter"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-[15px] text-[#8A948D]">
                Opening Balance
              </label>
              <Input
                value={formValues.openingBalance}
                onChange={(event) =>
                  updateField("openingBalance", event.target.value)
                }
                placeholder="Enter"
                className="h-12 rounded-[10px] border-none bg-[#F4F7FC] text-base shadow-none"
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="h-12 rounded-[10px] border-[#D6DDD0] px-8 text-[#7E8A80]"
              onClick={() => handleClose(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="h-12 rounded-[10px] bg-[#1A5EF9] px-8 text-white hover:bg-[#0E4FDE]"
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isSubmitting}
            >
              {isSubmitting ? (isEdit ? "Updating..." : "Submitting...") : (isEdit ? "Update" : "Submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NewVendorDialog;
